// Copyright 2025, University of Maryland, All Rights Reserved
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SectionRenderer from './renderers/SectionRenderer';

interface DynamicRendererProps {
  data: any;
  title?: string;
  className?: string;
}

/**
 * Dynamic renderer that can handle any JSON structure returned by LLM.
 * NO FALLBACKS - Only renders what actually exists in the data.
 * Ensures sections are rendered in priority order.
 * 
 * Supports both:
 * - Structured JSON responses (from normal comparisons)
 * - Unstructured text responses (from comparisons with "other" document type)
 *   Unstructured responses are rendered as markdown for proper formatting.
 */
const DynamicRenderer: React.FC<DynamicRendererProps> = ({ data, title, className = "" }) => {
  if (!data) {
    return (
      <div className={`bg-red-50 border border-red-200 rounded-lg p-4 ${className}`}>
        <p className="text-red-800 text-sm">
          ❌ No data received from analysis. The LLM response may have failed or returned empty results.
        </p>
      </div>
    );
  }

  // Handle unstructured text responses (when document type is "other")
  // These are markdown-formatted responses that need to be parsed
  if (typeof data === 'string') {
    return (
      <div className={`bg-white border border-gray-200 rounded-lg shadow-sm ${className}`}>
        <div className="p-6">
          {title && (
            <h2 className="text-2xl font-bold text-gray-800 mb-4">{title}</h2>
          )}
          {/* Render markdown with Tailwind typography styles and GFM support */}
          <div className="prose prose-slate prose-sm md:prose-base lg:prose-lg max-w-none">
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                // Customize heading styles for better hierarchy
                h1: ({node, ...props}) => <h1 className="text-3xl font-bold text-gray-900 mt-8 mb-4" {...props} />,
                h2: ({node, ...props}) => <h2 className="text-2xl font-bold text-gray-900 mt-6 mb-3" {...props} />,
                h3: ({node, ...props}) => <h3 className="text-xl font-semibold text-gray-900 mt-4 mb-2" {...props} />,
                // Customize list styles
                ul: ({node, ...props}) => <ul className="list-disc ml-6 space-y-2 my-4" {...props} />,
                ol: ({node, ...props}) => <ol className="list-decimal ml-6 space-y-2 my-4" {...props} />,
                li: ({node, ...props}) => <li className="pl-2" {...props} />,
                // Customize paragraph spacing
                p: ({node, ...props}) => {
                  // Check if paragraph contains only a number (like "1." or "2.")
                  const content = props.children?.toString().trim() || '';
                  const isNumberOnly = /^\d+\.$/.test(content);
                  
                  if (isNumberOnly) {
                    // Render standalone numbers with less vertical spacing
                    return <p className="mt-6 mb-2 text-gray-900 font-semibold text-lg" {...props} />;
                  }
                  
                  return <p className="my-3 text-gray-800 leading-relaxed" {...props} />;
                },
                // Style strong/bold text
                strong: ({node, ...props}) => <strong className="font-bold text-gray-900" {...props} />,
                // Style links if any
                a: ({node, ...props}) => <a className="text-blue-600 hover:text-blue-800 underline" {...props} />,
                // Table styling for proper rendering
                table: ({node, ...props}) => (
                  <div className="overflow-x-auto my-6">
                    <table className="min-w-full divide-y divide-gray-300 border border-gray-300" {...props} />
                  </div>
                ),
                thead: ({node, ...props}) => <thead className="bg-gray-50" {...props} />,
                tbody: ({node, ...props}) => <tbody className="divide-y divide-gray-200 bg-white" {...props} />,
                tr: ({node, ...props}) => <tr className="hover:bg-gray-50" {...props} />,
                th: ({node, ...props}) => (
                  <th className="px-3 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider border-r border-gray-200 last:border-r-0" {...props} />
                ),
                td: ({node, ...props}) => (
                  <td className="px-3 py-2 text-sm text-gray-700 border-r border-gray-200 last:border-r-0" {...props} />
                ),
              }}
            >
              {data}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    );
  }

  // Handle non-object types
  if (typeof data !== 'object') {
    return (
      <div className={`bg-gray-50 border rounded-lg p-4 ${className}`}>
        <p className="text-gray-800">{String(data)}</p>
      </div>
    );
  }

  // Define section priority order (lower number = higher priority = rendered first)
  const sectionPriority: Record<string, number> = {
    'executive_summary': 1,
    // Layered comparison sections (appear right after executive summary)
    'layer_1_document_structure': 2,
    'visualizations': 3,
    'layer_2_concept_coverage_analysis': 4,
    'layer_3_problem_by_problem_concept_analysis': 5,
    // Other sections
      'layer_4_pedagogical_insights': 6,
    'layer_5_recommendations': 7
  };
  
  // Default priority for unknown sections (rendered after known sections)
  const DEFAULT_PRIORITY = 999;
  
  // Sort all entries based on priority
  const orderedEntries = Object.entries(data).sort((a, b) => {
    const priorityA = sectionPriority[a[0]] ?? DEFAULT_PRIORITY;
    const priorityB = sectionPriority[b[0]] ?? DEFAULT_PRIORITY;
    return priorityA - priorityB;
  });

  return (
    <div className={`space-y-4 ${className}`}>
      {title && (
        <h2 className="text-xl font-bold text-gray-800 mb-3">{title}</h2>
      )}
      
      {orderedEntries.map(([key, value], index) => (
        <SectionRenderer
          key={`${key}-${index}`}
          sectionName={key}
          sectionData={value}
        />
      ))}
    </div>
  );
};

export default DynamicRenderer;